import { useId, useState } from 'react'
import type { JSX } from 'react'

import {
  Tooltip,
  Button,
  Text,
  Input,
  Textarea,
  Form,
  Flex,
  Badge,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import {
  selectHashtagGroupName,
  selectHashtagGroupText,
} from '~publish/legacy/hashtag-group-manager/selectors'
import type { HashtagGroup } from '~publish/legacy/hashtag-group-manager/types'
import { useAppSelector } from '~publish/legacy/store'

import {
  countRemainingHashtags,
  HASHTAG_LIMIT,
} from '../../utils/HashtagCounter'

import styles from './HashtagGroupForm.module.css'

interface HashtagGroupFormProps {
  hashtagGroup?: HashtagGroup
  onChangeGroupName: (name: string) => void
  onChangeGroupText: (text: string) => void
  onSubmit: ({ name, text }: { name: string; text: string }) => void
  onCancel: () => void
}

export const HashtagGroupForm = ({
  hashtagGroup,
  onChangeGroupName,
  onChangeGroupText,
  onSubmit,
  onCancel,
}: HashtagGroupFormProps): JSX.Element => {
  const nameId = useId()
  const textId = useId()
  const name = useAppSelector(selectHashtagGroupName)
  const text = useAppSelector(selectHashtagGroupText)

  const [nameError, setNameError] = useState<string | null>(null)
  const [textError, setTextError] = useState<string | null>(null)

  const remainingHashtagsCount = countRemainingHashtags(text)
  const hashtagLimitExceeded = remainingHashtagsCount < 0

  const handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setNameError(null)
    onChangeGroupName(event.target.value)
  }

  const handleTextChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    setTextError(null)
    onChangeGroupText(event.target.value)
  }

  const validate = (): boolean => {
    const hasNameError = !name?.trim()
    const hasTextError = !text?.trim()
    const hasHashtagLimitError = !hasTextError && hashtagLimitExceeded

    if (hasNameError) setNameError('Name is required')
    if (hasTextError) setTextError('Text is required')
    if (hasHashtagLimitError) setTextError('Hashtag limit exceeded')

    return !hasNameError && !hasTextError && !hasHashtagLimitError
  }

  const handleSubmit = (): void => {
    if (!validate()) {
      return
    }

    // if the text and name are the same, dismiss the form without saving
    if (
      text.trim() === hashtagGroup?.text.trim() &&
      name.trim() === hashtagGroup?.name.trim()
    ) {
      onCancel()
    } else {
      onSubmit({ name, text })
    }
  }

  return (
    <Form className={styles.form} autoComplete="off" onSubmit={handleSubmit}>
      <Form.Field name="hashtagGroupName">
        <VisuallyHidden as="label" htmlFor={nameId}>
          Hashtag Group Name
        </VisuallyHidden>
        <Form.Control>
          <Input
            id={nameId}
            type="text"
            value={name}
            maxLength={200}
            placeholder="Your hashtag group name"
            onChange={handleNameChange}
            onFocus={(): void => setNameError(null)}
            // The form usability is negatively impacted by not auto-focusing the name field
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            aria-invalid={!!nameError}
            aria-describedby={nameError ? `${nameId}:error` : undefined}
          />
        </Form.Control>
        {nameError && (
          <Text id={`${nameId}:error`} color="critical" size="sm">
            {nameError}
          </Text>
        )}
      </Form.Field>
      <Form.Field name="hashtagGroupContent" className={styles.textField}>
        <VisuallyHidden as="label" htmlFor={textId}>
          Hashtag Group Content
        </VisuallyHidden>
        <div className={styles.textAreaWrapper}>
          <Form.Control>
            <Textarea
              id={textId}
              value={text}
              maxLength={2000}
              placeholder="Your hashtags"
              className={styles.textarea}
              resize="none"
              rows={10}
              onChange={handleTextChange}
              onFocus={(): void => setTextError(null)}
              aria-invalid={!!textError}
              aria-describedby={textError ? `${textId}:error` : undefined}
            />
          </Form.Control>
          <Badge className={styles.badge} data-error={hashtagLimitExceeded}>
            <Tooltip
              content={`Instagram allows ${HASHTAG_LIMIT} hashtags per post.`}
              side="top"
            >
              <Text as="span" color="subtle">
                # Remaining: {remainingHashtagsCount}
              </Text>
            </Tooltip>
          </Badge>
        </div>
        {textError && (
          <Text id={`${textId}:error`} color="critical" size="sm">
            {textError}
          </Text>
        )}
      </Form.Field>
      <Flex gap="xs" justify="end" fullWidth>
        <Button type="button" variant="tertiary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">Save</Button>
      </Flex>
    </Form>
  )
}
